import { z } from 'zod'

// SAFT-AI federation JWT — the payload contract + role mapping. Signature /
// issuer / audience / expiry are verified in the `saftai-sso` edge function
// (RS256 against SAFT-AI's JWKS); this module is the claim shape + how their
// `app_role` maps onto our model. The edge function inlines an equivalent
// schema/mapping (Deno, esm.sh) — keep the two in sync (same pattern as the
// imap-poller ai.ts twin).

export const SaftaiAppRole = z.enum(['admin', 'team_lead', 'consultor', 'client'])
export type SaftaiAppRole = z.infer<typeof SaftaiAppRole>

// firm_id / sub may arrive as a JSON number or string — SAFT-AI emits firm_id as
// an int (e.g. 1). Coerce both to a non-empty string (→ firms.external_id).
const IdString = z
  .union([z.string(), z.number()])
  .transform((v) => String(v))
  .pipe(z.string().min(1))

export const SaftaiClaims = z.object({
  sub: IdString, // SAFT-AI user id
  email: z.string().email(),
  firm_id: IdString, // SAFT-AI tenant (accountant) id → our firms.external_id
  app_role: SaftaiAppRole,
  aal: z.string().optional(), // aal1/aal2 — read, not enforced (MFA owned by SAFT-AI)
  entitlement: z.string().optional(), // free | paid
})
export type SaftaiClaims = z.infer<typeof SaftaiClaims>

// Our `user_role` enum is admin|team_lead|consultor (staff, a consultants row);
// `client` is a portal user (client_users, no consultants row).
export type SaftaiPrincipal =
  | { kind: 'staff'; role: 'admin' | 'team_lead' | 'consultor' }
  | { kind: 'client' }

export function mapAppRole(appRole: SaftaiAppRole): SaftaiPrincipal {
  return appRole === 'client' ? { kind: 'client' } : { kind: 'staff', role: appRole }
}
